import { ChangeDetectorRef, Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { I18NService } from '@bocore';
import { AccessRightEnum } from '@boshared';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SettingsService, TitleService } from '@delon/theme';
import { AppConfig, environment } from '@env/environment';
import { AlertController, ModalController, Platform, PopoverController } from '@ionic/angular';
import { NzModalService } from 'ng-zorro-antd/modal';
import { catchError, EMPTY, forkJoin, take } from 'rxjs';
import { AccountApiService } from './core/services/api/account/account-api.service';
import { CommonApiService } from './core/services/api/common/common-api.service';
import { BROADCAST_SERVICE_TOKEN } from './core/services/helpers/broadcast.service';
import { NoticeDialogService } from './core/services/helpers/notice-dialog.service';
import { PwaUpdateService } from './core/services/helpers/pwa-update.service';
import { AuthService } from './core/services/implements/auth.service';
import { PageMemoryStateService } from './core/services/implements/page-memory-state.service';
import { PreferenceService } from './core/services/implements/preference.service';
import { AccountState } from './core/services/states/account.service';
import { ClientState } from './core/services/states/client.service';
import { CommonStorageService } from './core/services/states/common-storage.service';
import { DashboardMetricsState } from './core/services/states/dashboardMetrics.service';
import { FarmStorageService } from './core/services/states/farm-storage.service';
import { FarmState } from './core/services/states/farm.service';
import { MenuState } from './core/services/states/menu.service';
import { NetworkService } from './core/services/states/network.service';
import { TaskStorageService } from './core/services/states/task-storage.service';
import { TokenState } from './core/services/states/token.service';
import { ApiResponseCode } from './shared/constants/api-response-code';
import { BroadCastChannelMessageTypeConstant } from './shared/constants/broadcast-channel-message-type.constant';
import { SFStorageKeyConstant } from './shared/constants/sf-storage-key.constant';
import { GlobalService } from './shared/services/global-data/global.service';
import { PushNotificationService } from './shared/services/push-notification/push-notification.service';
import { StorageService } from './shared/services/storage/storage.service';
import { removeLocalStorage } from './shared/utils/storage.helper';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.less'],
  standalone: false,
  providers: [ScreenOrientation, I18NService]
})
export class AppComponent implements OnInit {
  protected destroyRef = inject(DestroyRef);
  protected platform = inject(Platform);
  protected globalData = inject(GlobalService);
  protected storageService = inject(StorageService);
  protected noticeDialogService = inject(NoticeDialogService);
  protected pwaUpdateService = inject(PwaUpdateService);
  protected settingsService = inject(SettingsService);
  protected authService = inject(AuthService);
  protected preferenceService = inject(PreferenceService);
  protected accountApiService = inject(AccountApiService);
  protected commonApiService = inject(CommonApiService);
  protected changeDetectorRef = inject(ChangeDetectorRef);
  protected clientState = inject(ClientState);
  protected farmState = inject(FarmState);
  protected tokenState = inject(TokenState);
  protected accountState = inject(AccountState);
  protected menuState = inject(MenuState);
  protected dashboardMetricState = inject(DashboardMetricsState);
  protected router = inject(Router);
  protected screenOrientation = inject(ScreenOrientation);
  protected broadcastService = inject(BROADCAST_SERVICE_TOKEN);
  protected titleService = inject(TitleService);
  protected appConfig = inject(AppConfig);
  protected modalController = inject(ModalController);
  protected popoverController = inject(PopoverController);
  protected alertController = inject(AlertController);
  protected nzModalService = inject(NzModalService);
  protected networkServ = inject(NetworkService);
  protected taskStorageServ = inject(TaskStorageService);
  protected commonStorageServ = inject(CommonStorageService);
  protected farmStorageServ = inject(FarmStorageService);
  protected pushNotificationService = inject(PushNotificationService);
  protected pageMemoryStateService = inject(PageMemoryStateService);

  webScreen: number = 1200;
  exitPages: Array<string> = ['/', '/dashboard'];

  @HostListener('window:resize')
  onWindowResize() {
    this.checkWebStyle();
  }

  constructor() {
    this.globalData.isMobileStyle = environment.platform === 'mobile';
    document.body.classList.add(this.globalData.isMobileStyle ? 'mobile' : 'web');

    this.networkServ.init();
  }

  ngOnInit(): void {
    this.initializeApp();
    this.pwaUpdateService.init();
    this.initializeSubscription();
    this.initializeAuthService();
    this.initPreferenceService();
  }

  initializeApp() {
    this.checkWebStyle();
    this.platform.ready().then(() => {
      if (this.platform.is('android')) {
        // Ensure status bar sits above the web view instead of overlaying it.
        if (Capacitor.isNativePlatform()) {
          StatusBar.setOverlaysWebView({ overlay: true });
          StatusBar.setBackgroundColor({ color: '#0f594e' });
          StatusBar.setStyle({ style: Style.Dark });
        }

        App.addListener('backButton', () => {
          this.backButtonListener();
        });
      }
    });
    // this.checkDevice();
    // this.setRefIdAndBtagCookies();
    // this.pushToClientService.initPushToClient();

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(ev => {
      if (ev instanceof NavigationEnd) {
        this.titleService.setTitle();
      }
    });
  }
  initializeSubscription() {
    // Subscribe to login broadcast
    this.broadcastService
      .messagesOfType(BroadCastChannelMessageTypeConstant.LoginApp)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        setTimeout(() => {
          this.accountState.init();
          this.menuState.init();
          this.clientState.initClientList();
          this.farmState.initFarmList();
        }, 600);
        this.router.navigate(['/dashboard']);
      });

    // Subscribe to logout broadcast
    this.broadcastService
      .messagesOfType(BroadCastChannelMessageTypeConstant.LogoutApp)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.authService.clearLoginSession();
        this.router.navigate(['/auth/login']);
      });

    // Subscribe to language change broadcast
    this.broadcastService
      .messagesOfType(BroadCastChannelMessageTypeConstant.Language)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.preferenceService.getPreference();
      });

    // Subscribe to theme change broadcast
    this.broadcastService
      .messagesOfType(BroadCastChannelMessageTypeConstant.Theme)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.preferenceService.getPreference();
      });

    // Subscribe to account info change broadcast
    this.broadcastService
      .messagesOfType(BroadCastChannelMessageTypeConstant.AccountInfo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.accountState.init();
      });
  }
  private initPreferenceService(): void {
    this.preferenceService.setUpdatePreferenceLogic((language: string, theme: string, callback?: () => void) => {
      this.accountApiService
        .UpdateUserSetting({
          defaultLanguage: language,
          defaultTheme: theme
        })
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(res => {
          if (callback) {
            callback();
          }
        });
    });
  }
  private initializeAuthService() {
    this.authService.setLoginLogic((username: string, password: string, callback?: () => void) => {
      this.accountApiService
        .UserLogin({ loginId: username, password: password })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            if (callback) {
              callback();
            }
            return EMPTY;
          })
        )
        .subscribe(t => {
          if (t.code === ApiResponseCode.Success) {
            const loginResult = t.data.loginInfo;
            if (!loginResult.menuList.length) {
              this.noticeDialogService.showError('common.message.httpError.accessDenied', '', () => this.authService.logout(true));
              if (callback) {
                callback();
              }
            } else {
              this.tokenState.setState(loginResult.tokenInfo);
              this.accountState.setState(loginResult.accountInfo);
              this.menuState.setState(loginResult.menuList);
              this.dashboardMetricState.setState(loginResult.dashboardMetricList);
              this.menuState.init();

              // Initialize push notifications only for native app (apk/ipa)
              this.pushNotificationService.subscribeNotification();

              forkJoin([
                this.commonApiService.GetClientDropdown({}).pipe(takeUntilDestroyed(this.destroyRef)),
                this.commonApiService.GetUserFarmDropdown({}).pipe(takeUntilDestroyed(this.destroyRef))
              ])
                .pipe(
                  takeUntilDestroyed(this.destroyRef),
                  catchError(() => {
                    if (callback) {
                      callback();
                    }
                    return EMPTY;
                  })
                )
                .subscribe(([clientResponse, farmResponse]) => {
                  this.clientState.clear(); // always clear state instead of remember previous selection
                  this.farmState.clear(); // always clear state instead of remember previous selection

                  // Handle offline data according to platform and permission
                  if (this.globalData.isMobileStyle && this.menuState.hasPermissionWithUrl('/task/list', [AccessRightEnum.Search])) {
                    // Load offline data subcription
                    this.commonStorageServ.load();
                    this.farmStorageServ.load();
                    this.taskStorageServ.load();
                  } else {
                    // Remove task offline data
                    this.taskStorageServ.clear();
                  }

                  const clientData = clientResponse.data.model;
                  this.clientState.setClientList(
                    clientData.map(m => ({
                      clientId: m.code,
                      clientName: m.label
                    }))
                  );

                  const farmData = farmResponse.data?.model;
                  this.farmState.setFarmList(
                    farmData.map(m => ({
                      farmId: +m.code,
                      farmName: m.label,
                      clientId: m.clientId,
                      address: m.address,
                      longitude: m.longitude,
                      latitude: m.latitude,
                      metricList: m.metricList
                    })),
                    this.clientState.get()?.clientId
                  );

                  this.broadcastService.publish({
                    type: BroadCastChannelMessageTypeConstant.LoginApp,
                    payload: null
                  });

                  this.router.navigate(['/dashboard']);
                  this.preferenceService.changeLanguage(loginResult.accountInfo.defaultLanguage).subscribe(() => {
                    this.broadcastService.publish({
                      type: BroadCastChannelMessageTypeConstant.Language,
                      payload: null
                    });
                  });
                  this.preferenceService.changeTheme(loginResult.accountInfo.defaultTheme).subscribe(() => {
                    this.broadcastService.publish({
                      type: BroadCastChannelMessageTypeConstant.Theme,
                      payload: null
                    });
                  });
                });
            }
          } else {
            if (callback) {
              callback();
            }
          }
        });
    });

    this.authService.setLogoutLogic(async (callApi?: boolean) => {
      // Ensure FCM token is removed from the device (even when offline)
      await this.pushNotificationService.unsubscribeNotification();

      if (callApi) {
        this.accountApiService.UserLogout().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      }
      this.tokenState.clear();
      this.accountState.removeFromStorage();
      this.menuState.removeFromStorage();
      this.clientState.removeFromStorage();
      this.farmState.removeFromStorage();
      this.dashboardMetricState.removeFromStorage();
      this.pageMemoryStateService.clearAllPageStates();
      removeLocalStorage(SFStorageKeyConstant.ClientList);
      removeLocalStorage(SFStorageKeyConstant.FarmList);

      this.commonStorageServ.unsubscribe();
      this.farmStorageServ.unsubscribe();
      this.taskStorageServ.unsubscribe();

      this.router.navigate(['/auth/login']);

      this.broadcastService.publish({
        type: BroadCastChannelMessageTypeConstant.LogoutApp,
        payload: {}
      });
    });
  }
  async backButtonListener() {
    for (const ctrl of [this.popoverController, this.alertController, this.modalController]) {
      const top = await ctrl.getTop();
      if (top) {
        await top.dismiss();
        return;
      }
    }

    const topNzModal = this.nzModalService.openModals.at(-1);
    if (topNzModal) {
      topNzModal.close();
      return;
    }

    const currentRoute = this.router.url;
    // 允許離開APP的頁面
    if (this.exitPages.find(p => p === currentRoute)) {
      App.exitApp();
    } else {
      window.history.back();
      this.changeDetectorRef.detectChanges();
    }
  }

  checkWebStyle() {
    const url = window.location.pathname.toLocaleLowerCase();
    // if (window.innerWidth < this.webScreen || this.globalData.isApp) {
    //   this.globalData.isMobileStyle = true;
    // } else {
    //   this.globalData.isMobileStyle = false;
    // }

    let status = this.storageService.getStorage('resize');
    if (window.innerWidth >= this.webScreen) {
      if (status !== 'websize') {
        this.storageService.setStorage('resize', 'websize');
        // window.location.reload();
      }
    } else {
      if (status !== 'appsize') {
        this.storageService.setStorage('resize', 'appsize');
        // window.location.reload();
      }
    }
  }

  setAPPOrientation(event: any) {
    if (this.globalData.isApp) {
      if (event.url.includes('/Games?')) {
        this.screenOrientation.unlock();
      } else {
        this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.PORTRAIT);
      }
    }
  }
}
